import React, {
  useState,
  useEffect,
  forwardRef,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
// import PropTypes from 'prop-types'
import {
  InputDatePickerBetweenStyled,
  InputDatePickerBetweenPopupStyle,
} from './styled';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  MdChevronLeft,
  MdChevronRight,
  MdArrowDropDown,
} from 'react-icons/md';
import { Icons } from 'components';

const popperContainer = ({ children }) =>
  typeof document !== 'undefined'
    ? createPortal(children, document.body)
    : null;

const MONTHS = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];

const range = (start, end) => {
  const result = [];
  for (let i = start; i <= end; i += 1) result.push(i);
  return result;
};

const CalendarSelect = ({ value, options, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`dp_cs ${className || ''}`} ref={ref}>
      <button
        type="button"
        className="dp_cs_btn"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dp_cs_label">{selected?.label}</span>
        <MdArrowDropDown size={18} />
      </button>
      {open && (
        <ul className="dp_cs_menu" role="listbox">
          {options.map((o) => {
            const isActive = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isActive}
                className={`dp_cs_item ${isActive ? 'active' : ''}`}
                ref={
                  isActive
                    ? (el) => el && el.scrollIntoView({ block: 'nearest' })
                    : null
                }
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const InputDatePickerBetween = ({
  theme_standard,
  label,
  startDatePlaceholder,
  endDatePlaceholder,
  onChange,
  // errors,
  disabled,
  value,
  yearRange,
  // label,
}) => {
  const customClass = cx({
    theme_standard: theme_standard,
  });

  const [_startDate, _setStartDate] = useState();
  const [_endDate, _setEndDate] = useState();

  useEffect(() => {
    if (value) {
      if (value.startDate) {
        _setStartDate(value.startDate);
      }
      if (value.endDate) {
        _setEndDate(value.endDate);
      }
    }
  }, [value]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const from = yearRange?.from ?? currentYear - 50;
    const to = yearRange?.to ?? currentYear + 5;
    return range(from, to);
  }, [yearRange]);

  const monthOptions = useMemo(
    () => MONTHS.map((m, i) => ({ label: m, value: i })),
    []
  );
  const yearOptions = useMemo(
    () => years.map((y) => ({ label: String(y), value: y })),
    [years]
  );

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="dp_header">
      <button
        type="button"
        className="dp_nav_btn"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        <MdChevronLeft size={20} />
      </button>
      <div className="dp_selects">
        <CalendarSelect
          className="dp_cs_month"
          value={date.getMonth()}
          options={monthOptions}
          onChange={(v) => changeMonth(v)}
        />
        <CalendarSelect
          className="dp_cs_year"
          value={date.getFullYear()}
          options={yearOptions}
          onChange={(v) => changeYear(v)}
        />
      </div>
      <button
        type="button"
        className="dp_nav_btn"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        <MdChevronRight size={20} />
      </button>
    </div>
  );

  const ExampleCustomInput1 = forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      className="custom_date"
      onClick={onClick}
      ref={ref}
      disabled={disabled}
    >
      <div className="show_value">
        {value
          ? `${moment(value).format('DD/MM/YYYY')}`
          : startDatePlaceholder}
      </div>
      <div className="arrow_icon">
        <Icons.ArrowDropdown transform="translate(13 11) rotate(180)" />
      </div>
    </button>
  ));

  const ExampleCustomInput2 = forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      className="custom_date"
      onClick={onClick}
      ref={ref}
      disabled={disabled}
    >
      <div className="show_value">
        {value ? `${moment(value).format('DD/MM/YYYY')}` : endDatePlaceholder}
      </div>
      <div className="arrow_icon">
        <Icons.ArrowDropdown transform="translate(13 11) rotate(180)" />
      </div>
    </button>
  ));

  const _handleChangePicker = (key, value) => {
    switch (key) {
      case 'startDate':
        _setStartDate(value);
        onChange && onChange({ startDate: value, endDate: _endDate });
        break;
      case 'endDate':
        _setEndDate(value);
        onChange && onChange({ startDate: _startDate, endDate: value });
        break;
      default:
        break;
    }
  };

  return (
    <InputDatePickerBetweenStyled>
      <InputDatePickerBetweenPopupStyle />
      <div className={customClass}>
        {label && <div className="label">{label}</div>}
        <div className="date_between_container">
          <DatePicker
            className="date_picker"
            selected={_startDate}
            onChange={(e) => {
              _handleChangePicker('startDate', e);
            }}
            customInput={<ExampleCustomInput1 />}
            calendarClassName="dp_calendar"
            renderCustomHeader={renderCustomHeader}
            disabled={disabled}
            maxDate={_endDate}
            popperContainer={popperContainer}
            popperClassName="ipdb_dp_popper"
          />

          <DatePicker
            className="date_picker"
            selected={_endDate}
            onChange={(e) => {
              _handleChangePicker('endDate', e);
            }}
            customInput={<ExampleCustomInput2 />}
            calendarClassName="dp_calendar"
            renderCustomHeader={renderCustomHeader}
            disabled={disabled}
            minDate={_startDate}
            popperContainer={popperContainer}
            popperClassName="ipdb_dp_popper"
          />
        </div>
      </div>
    </InputDatePickerBetweenStyled>
  );
};

InputDatePickerBetween.propTypes = {};

export default InputDatePickerBetween;
